<script>
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import { toDecPercent, toMoney, truncateText } from '@utils/Format'
import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'

const LABEL_MAX_LENGTH = 20

export default {
  name: 'GenericOverviewDonutChart',
  components: { FeedbackWrapper },
  props: {
    // Mesmo default do FeedbackWrapper (400): o template passa a prop SEMPRE,
    // entao um default menor rebaixaria o piso dos outros consumidores.
    minHeight: { type: Number, default: 400 },
    // Default vazio = valor cru, como sempre foi (o `graphs-card-list` conta
    // ocorrencias, nao dinheiro). Quem passa `real`/`dol` ganha a mascara.
    currency: { type: String, default: '' },
    chartData: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      required: true,
    },
    baseKey: {
      type: String,
      default: null,
    },
    maxItems: {
      type: Number,
      default: 7,
    },
    datasetLabel: {
      type: String,
      default: 'Valor',
    },
    useSum: {
      type: Boolean,
      default: false,
    },
    noDataTitle: {
      type: String,
      default: 'Sem dados no momento.',
    },
    noDataDescription: {
      type: String,
      default:
        'Os dados podem não terem sido cadastrados ou não estão no recorte dos filtros selecionados.',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    processedData() {
      const { percentData, valueData } = this.getSeriesAndValue()
      const items = this.sortAndComputeItems(percentData, valueData)

      return {
        labels: items.map((item) => item.label),
        percents: items.map((item) => item.percent),
        values: items.map((item) => item.value),
      }
    },
    chartConfig() {
      return {
        data: {
          labels: this.processedData.labels,
          datasets: [
            {
              data: this.processedData.percents.map((p) => p * 100),
              backgroundColor: this.processedData.labels.map((_, i) =>
                this.getColor(i)
              ),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          tooltips: {
            callbacks: {
              title: (items, data) => {
                if (!items || !items.length) return ''
                return this.truncateLabel(data.labels[items[0].index])
              },
              label: (tooltipItem, data) => {
                const index = tooltipItem.index
                const percent = data.datasets[0].data[index] / 100
                const value = this.processedData.values[index]
                return `${toDecPercent(percent)}: ${this.formatValue(value)}`
              },
            },
          },
          legend: {
            display: false,
          },
        },
      }
    },
    legends() {
      return this.processedData.labels.map((label, index) => ({
        label,
        percent: this.processedData.percents[index],
        color: this.getColor(index),
        value: this.processedData.values[index],
      }))
    },
    isSmall() {
      if (this.$vuetify.breakpoint) {
        return this.$vuetify.breakpoint.mdAndDown
      } else {
        return false
      }
    },
    hasData() {
      return this.processedData.labels.length > 0
    },
  },
  methods: {
    getSeriesAndValue() {
      const keyBase = this.baseKey || this.title.toLowerCase()
      const chartData = this.chartData || {}
      const percentData = chartData[`${keyBase}_percent`] || {}
      const countData = chartData[`${keyBase}_count`] || {}
      const sumData = chartData[`${keyBase}_sum_auc`] || {}
      const valueData = this.useSum ? sumData : countData
      return { percentData, valueData }
    },
    sortAndComputeItems(percentData, valueData) {
      const dataArray = Object.entries(percentData).map(([label, percent]) => ({
        label,
        percent,
        value: valueData[label] || 0,
      }))

      const sortedData = dataArray.sort((a, b) => b.percent - a.percent)
      const topItems = sortedData.slice(0, this.maxItems)
      const extras = sortedData.slice(this.maxItems)

      const othersTotal = extras.reduce((sum, item) => sum + item.percent, 0)
      const othersValue = extras.reduce((sum, item) => sum + item.value, 0)

      const items = [...topItems]
      if (othersTotal > 0) {
        items.push({
          label: 'Outros',
          percent: othersTotal,
          value: othersValue,
        })
      }

      return items
    },
    getColor(index) {
      return REPORT_PALETTE[index % REPORT_PALETTE.length]
    },
    formatTooltip(legend) {
      return `${legend.label} (${toDecPercent(legend.percent)}): ${this.formatValue(
        legend.value
      )}`
    },
    formatValue(value) {
      if (!this.currency) return value
      return toMoney(value, 2, this.currency)
    },
    truncateLabel(label) {
      const text = label === null || label === undefined ? '' : String(label)
      return truncateText({ text, maxLength: LABEL_MAX_LENGTH })
    },
  },
}
</script>

<template>
  <FeedbackWrapper
    :loading="loading"
    :noData="!hasData"
    :minHeight="minHeight"
    :title="title"
    message=""
    iconName=""
    class="rounded-sm"
    titleClass="az-ds-card-title"
    bordered
  >
    <template v-slot:content>
      <div class="d-flex flex-column az-gap-xsmall">
        <div class="az-text-secondary text-center">
          {{ noDataTitle }}
        </div>
        <div class="az-text-secondary text-sm text-center">
          {{ noDataDescription }}
        </div>
      </div>
    </template>

    <div class="generic-donut-chart az-card-bordered">
      <div v-if="hasData" class="generic-donut-chart__title az-ds-card-title">
        {{ title }}
      </div>

      <div class="generic-donut-chart__content">
        <div class="generic-donut-chart__graph">
          <donut-chart
            :chartData="chartConfig.data"
            :options="chartConfig.options"
            :width="isSmall ? 150 : 375"
            :height="isSmall ? 150 : 250"
          />
        </div>
        <div class="generic-donut-chart__legends">
          <div
            v-for="(legend, index) in legends"
            :key="index"
            class="generic-donut-chart__legend"
          >
            <div
              class="generic-donut-chart__legend-color"
              :style="{ backgroundColor: legend.color }"
            ></div>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span class="generic-donut-chart__legend-label" v-on="on">
                  {{ legend.label }}
                </span>
              </template>
              <span>{{ formatTooltip(legend) }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>
  </FeedbackWrapper>
</template>

<style lang="sass">
.generic-donut-chart
  height: 100%

  &__title
    margin-bottom: 56px

  &__content
    display: flex
    align-items: center
    justify-content: center

  &__graph
    min-width: 250px

  &__legend
    display: flex
    align-items: center
    margin-bottom: 8px

  &__legend-color
    width: 21px
    height: 8px
    margin-right: 8px
    flex-shrink: 0

  &__legend-label
    font-size: 0.75rem
    color: var(--secondary)
    text-overflow: ellipsis
    white-space: nowrap
    overflow: hidden
    max-width: 150px
    display: block
</style>
